/**
 * @file Renders the searchable list of organizations the current user can switch to.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Space, Spin } from 'antd';
import type { InputRef } from 'antd';

import Table from '@components/Table';
import { getConfig } from '@components/Config';
import { SearchInput } from '@components/Page';
import { useAppContext } from '@components/AppContext';
import { type AppContext } from '@components/AppContext/types';
import { indexOrganizationsOperation as indexOperation } from '@api/services/organizations';

import './OrganizationsList.css';

import useOrganizations from './helpers/useOrganizations';
import useColumnsConfig from './helpers/useColumnsConfig';

const pinnedOrganizationId = getConfig<string>('PINNED_ORG_ID');

type Props = {
  onAfterSwitch?: () => Promise<void> | void;
};

type TableRef = {
  search: (value: string) => void;
};

/**
 * Renders the searchable organizations table, highlighting the organization that the enter key switches to:
 * the first search match, or the pinned organization of the environment when there is no search query.
 */
const OrganizationsList = ({ onAfterSwitch: onAfterSwitchCustom }: Props) => {
  const table = useRef<TableRef>({
    /**
     * No-op placeholder until the table sets its real search handler.
     */
    search: () => undefined,
  });
  const searchInputRef = useRef<InputRef>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const items = useOrganizations();
  const {
    identity,
    switchToOrganization,
    authorizeOrganizationIdentity,
  } = useAppContext() as AppContext;

  /**
   * Shows the loading spinner before the user switches organizations.
   */
  const onBeforeSwitch = async () => setIsLoading(true);
  const onAfterSwitch = onAfterSwitchCustom || (() => undefined);

  const currentOrganizationId = identity?.organization?.id;

  const defaultOrganization = useMemo(() => {
    const hasQuery = searchQuery.length > 0;

    if (hasQuery) {
      const firstMatchedOrganization = items.find(({ id, name }) => (
        id !== currentOrganizationId
          && name.toLowerCase().includes(searchQuery.toLowerCase())
      ));

      return firstMatchedOrganization;
    }

    const pinnedOrganization = items.find(({ id }) => id === pinnedOrganizationId);
    const isPinnedOrganizationCurrent = pinnedOrganization?.id === currentOrganizationId;

    if (isPinnedOrganizationCurrent) {
      return undefined;
    }

    return pinnedOrganization;
  }, [ items, searchQuery, currentOrganizationId ]);

  const highlightedOrganizationId = defaultOrganization?.id;

  const columnsConfig = useColumnsConfig({
    onBeforeSwitch,
    onAfterSwitch,
    highlightedOrganizationId,
  });

  const pagination = {
    pageSize: 10,
    showSizeChanger: false,
  };

  const tableProps = {
    /**
     * Reloads the organizations table by re-authorizing the current organization identity.
     */
    indexItems: () => authorizeOrganizationIdentity(),
    size: 'small',
    items,
    table,
    pagination,
    columnsConfig,
    indexOperation,
    /**
     * Highlights the table row for the organization that the enter key switches to.
     */
    onRow: record => {
      const shouldHighlightRow = Boolean(
        highlightedOrganizationId && record?.id === highlightedOrganizationId,
      );

      if (!shouldHighlightRow) {
        return {};
      }

      return {
        className: 'organizations-search-highlight',
      };
    },
  };

  /**
   * Updates the search query and filters the table to match it.
   */
  const onSearch = (value: string) => {
    setSearchQuery(value);
    table.current.search(value);
  };

  /**
   * Switches to the highlighted organization: the first search match, or the pinned one without a query.
   */
  const onPressEnter = async (): Promise<void> => {
    const hasDefaultOrganization = Boolean(defaultOrganization);

    if (!hasDefaultOrganization || !defaultOrganization) {
      return;
    }

    await onBeforeSwitch();
    await switchToOrganization(defaultOrganization.id);
    await onAfterSwitch();
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Spin spinning={isLoading}>
      <Space
        style={{ width: '100%' }}
        direction="vertical"
      >
        <SearchInput
          minCharacters={2}
          onSearch={onSearch}
          inputProps={{
            autoFocus: true,
            ref: searchInputRef,
            onPressEnter,
          }}
        />

        <Table {...tableProps} />
      </Space>
    </Spin>
  );
};

export default OrganizationsList;
